<template>
  <div class="initial-step step-inner-padded d-flex justify-content-center">
    <div class="card" style="width: 600px">
      <div class="card-body p-4">
        <!-- Header Section -->
        <div class="d-flex justify-content-between align-items-start mb-4">
          <div>
            <h3 class="mb-2">{{ $t('order_creation.initial_step.main_heading') }}</h3>
            <p class="text-muted mb-0">
              {{ $t('order_creation.initial_step.main_description') }}
            </p>
          </div>
        </div>

        <div class="row g-2 mb-4">
          <div class="col-6 col-md-3">
            <div
              class="card option-card"
              data-testid="oc-source-direct-url"
              :class="{ 'border-primary bg-primary-subtle': order.creation_source === 'direct_url' }"
              @click="order.creation_source = 'direct_url'">
              <div class="card-body p-3">
                <div class="mb-2">
                  <IconUnlink :size="24" :class="[order.creation_source === 'direct_url' ? 'text-primary' : '']" />
                </div>
                <h6 class="card-title">{{ $t('order_creation.initial_step.option_direct_url_title') }}</h6>
                <p class="text-muted">{{ $t('order_creation.initial_step.option_direct_url_description') }}</p>
              </div>
            </div>
          </div>

          <div class="col-6 col-md-3">
            <div
              class="card option-card"
              data-testid="oc-source-file-import"
              :class="{ 'border-primary bg-primary-subtle': order.creation_source === 'file_import' }"
              @click="order.creation_source = 'file_import'">
              <div class="card-body p-3">
                <div class="mb-2">
                  <IconFileIsr :size="32" :class="[order.creation_source === 'file_import' ? 'text-primary' : '']" />
                </div>
                <h6 class="card-title">{{ $t('order_creation.initial_step.option_file_import_title') }}</h6>
                <p class="text-muted">{{ $t('order_creation.initial_step.option_file_import_description') }}</p>
              </div>
              <div class="funnel-icon" v-tooltip="$t('order_creation.initial_step.lead_funnel_tooltip')">
                <IconChartFunnel :size="10" class="text-white" />
              </div>
            </div>
          </div>

          <div class="col-6 col-md-3">
            <div
              class="card option-card"
              data-testid="oc-source-web-import"
              :class="{ 'border-primary bg-primary-subtle': order.creation_source === 'web_import' }"
              @click="order.creation_source = 'web_import'">
              <div class="card-body p-3">
                <div class="mb-2">
                  <IconWorldWww :size="32" :class="[order.creation_source === 'web_import' ? 'text-primary' : '']" />
                </div>
                <h6 class="card-title">{{ $t('order_creation.initial_step.option_web_import_title') }}</h6>
                <p class="text-muted">{{ $t('order_creation.initial_step.option_web_import_description') }}</p>
              </div>
              <div class="funnel-icon" v-tooltip="$t('order_creation.initial_step.lead_funnel_tooltip')">
                <IconChartFunnel :size="10" class="text-white" />
              </div>
            </div>
          </div>

          <div class="col-6 col-md-3">
            <div
              class="card option-card"
              data-testid="oc-source-custom"
              :class="{ 'border-primary bg-primary-subtle': order.creation_source === 'custom' }"
              @click="order.creation_source = 'custom'">
              <div class="card-body p-3">
                <div class="mb-2">
                  <IconAd2 :size="32" :class="[order.creation_source === 'custom' ? 'text-primary' : '']" />
                </div>
                <h6 class="card-title">{{ $t('order_creation.initial_step.option_custom_title') }}</h6>
                <p class="text-muted">{{ $t('order_creation.initial_step.option_custom_description') }}</p>
              </div>
              <div class="funnel-icon" v-tooltip="$t('order_creation.initial_step.lead_funnel_tooltip')">
                <IconChartFunnel :size="10" class="text-white" />
              </div>
            </div>
          </div>
        </div>
        <hr class="section-divider" />
        <!-- URL Input Section (shown when Direkt-URL is selected) -->
        <div v-if="order.creation_source === 'direct_url'" class="pt-4">
          <h3 class="mb-3">{{ $t('order_creation.initial_step.direct_url_section_heading') }}</h3>
          <p class="text-muted mb-3">
            {{ $t('order_creation.initial_step.direct_url_section_description') }}
          </p>

          <div class="mb-3" data-testid="oc-direct-url-input">
            <LeadInput
              v-model="order.directlink"
              :label="$t('order_creation.initial_step.job_url_label')"
              @blur="onLinkBlur"
              :placeholder="$t('order_creation.initial_step.job_url_placeholder')"
              @paste="onLinkPaste"
              @input="onLinkInput">
              <template #prefix> <span>https://</span> </template>
            </LeadInput>
          </div>

          <div
            class="d-flex align-items-center cursor-pointer"
            data-testid="oc-apply-url-open"
            @click="openApplyLinkForm"
            v-if="!showActiveLinkForm">
            <IconCirclePlus :size="16" class="text-muted me-2" />
            <a href="#" class="text-dark text-decoration-underline">{{
              $t('order_creation.initial_step.application_form_link')
            }}</a>
          </div>
          <div class="d-flex justify-content-between align-items-start mb-3" v-else>
            <div class="w-100 me-3" data-testid="oc-apply-url-input">
              <LeadInput
                v-model="order.apply_url"
                :label="$t('order_creation.initial_step.apply_url_label')"
                :placeholder="$t('order_creation.initial_step.job_url_placeholder')"
                @input="onApplyLinkChange"
                :hint="$t('order_creation.initial_step.apply_url_hint')">
                <template #prefix> <span>https://</span> </template>
              </LeadInput>
            </div>
            <button
              class="btn btn-icon btn-ghost-secondary remove-active-link"
              data-testid="oc-apply-url-close"
              v-tooltip="$t('order_creation.initial_step.no_apply_url_tooltip')"
              @click="removeApplyLink">
              <IconX :size="16" class="cursor-pointer" />
            </button>
          </div>
        </div>

        <!-- Import Section (shown when Datei-import is selected) -->
        <div v-else-if="order.creation_source === 'file_import'" class="pt-4">
          <h3 class="mb-3">{{ $t('order_creation.initial_step.file_import_section_heading') }}</h3>
          <p class="text-muted mb-3">
            {{ $t('order_creation.initial_step.file_import_section_description') }}
          </p>
          <DropFile
            v-model="fileImports"
            formDataName="file"
            :mediaCount="1"
            :showList="true"
            :accept="fileAcceptAttribute"
            :validationFn="validateFileUpload"
            :uploadFn="importStore.uploadAndExtractFile"
            @fileRemoved="onFileRemoved"
            class="file-drop-zone" />
        </div>

        <!-- Web Import Section -->
        <div v-else-if="order.creation_source === 'web_import'" class="pt-4">
          <h3 class="mb-3">{{ $t('order_creation.initial_step.web_import_section_heading') }}</h3>
          <p class="text-muted mb-3">
            {{ $t('order_creation.initial_step.web_import_section_description') }}
          </p>
          <div data-testid="oc-web-import-url-input">
            <LeadInput
              v-model="webImportUrl"
              :label="$t('order_creation.initial_step.job_url_label')"
              @paste="onLinkPaste"
              :placeholder="$t('order_creation.initial_step.job_url_placeholder')"
              @blur="onLinkBlur"
              @input="onLinkInput">
              <template #prefix> <span>https://</span> </template>
            </LeadInput>
          </div>
        </div>

        <!-- Custom Section -->
        <div v-else-if="order.creation_source === 'custom'" class="pt-4">
          <h3 class="mb-3">{{ $t('order_creation.initial_step.custom_section_heading') }}</h3>
          <p class="text-muted mb-4">
            {{ $t('order_creation.initial_step.custom_section_description') }}
          </p>
          <div class="d-flex mb-3">
            <LeadInput v-model="order.initial_job_posting" type="switch" class="w-fit"> </LeadInput>
            <div class="d-flex flex-column">
              <h4 class="mb-0">{{ $t('order_creation.initial_step.content_generation_switch_label') }}</h4>
              <p class="text-muted">{{ $t('order_creation.initial_step.content_generation_switch_description') }}</p>
            </div>
            <IconSparkles color="currentColor" class="tw:text-primary" stroke-width="1.5" size="24" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  IconCirclePlus,
  IconWorldWww,
  IconUnlink,
  IconFileIsr,
  IconAd2,
  IconChartFunnel,
  IconX,
  IconSparkles,
} from '@tabler/icons-vue';
import { useOrderCreationStore } from '@/stores/orderCreation';
import { storeToRefs } from 'pinia';
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import LeadInput from '@/components/form/LeadInput.vue';
import { useImportOrderCreationStore } from '@/stores/importOrderCreation';
import DropFile from '@/components/common/DropZone/DropZone.vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const orderCreationStore = useOrderCreationStore();
const { order, customContentGeneration } = storeToRefs(orderCreationStore);

const importStore = useImportOrderCreationStore();
const { webImportUrl, fileImports, fileUploaded, fileIsUploading } = storeToRefs(importStore);
const allowNext = defineModel<boolean>('allowNext');

/* ---------------------------------- STATE --------------------------------- */
const showActiveLinkForm = ref<boolean>(false);
const skipNextBlurExtraction = ref<boolean>(false);

/* -------------------------------- LIFECYCLE ------------------------------- */
onMounted(() => {
  if (!order.value.creation_source) order.value.creation_source = 'direct_url';
  validateFields();
  if (order.value.creation_source === 'direct_url' && order.value.apply_url?.length) showActiveLinkForm.value = true;
});
onBeforeUnmount(() => {
  // If there is an orderId in the URL, we assume the user is editing an existing order, skip resetting
  if (route.query.orderId) return;

  if (order.value.creation_source === 'file_import' || order.value.creation_source === 'custom') {
    if (order.value.directlink) order.value.directlink = '';
    if (order.value.apply_url) order.value.apply_url = '';
  }
  if (order.value.creation_source !== 'web_import' && webImportUrl.value) webImportUrl.value = '';
  if (order.value.creation_source !== 'custom' && customContentGeneration.value) customContentGeneration.value = false;
  if (order.value.creation_source !== 'file_import') {
    if (fileImports.value.length) fileImports.value = [];
    importStore.resetFileUploadState();
  }
});

/* --------------------------------- METHODS -------------------------------- */
function stripHttps(url: string): string {
  return url
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .trim();
}

function validateUrl(url: string): boolean {
  if (!url || url.trim().length === 0) return false;

  // Strip http:// or https:// if present
  const cleanUrl = stripHttps(url);

  // Check for spaces first - URLs cannot contain spaces
  if (/\s/.test(cleanUrl)) return false;

  // More comprehensive URL validation pattern
  // Domain: letters, numbers, hyphens, dots (no consecutive dots, no leading/trailing hyphens)
  // Port: optional :port
  // Path: letters, numbers, hyphens, underscores, dots, forward slashes, percent-encoding
  // Query: letters, numbers, hyphens, underscores, dots, equals, ampersands, percent-encoding
  // Fragment: letters, numbers, hyphens, underscores, dots, percent-encoding
  const urlPattern =
    /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(:[0-9]{1,5})?(\/[a-zA-Z0-9._~:/?#[\]@!$&'()*+,;=%_-]*)?$/;

  return urlPattern.test(cleanUrl) && cleanUrl.length > 0;
}

function validateFields() {
  let isValid = false;

  if (order.value.creation_source === 'direct_url') {
    const direct = validateUrl(order.value.directlink || '');
    const apply = !showActiveLinkForm.value || validateUrl(order.value.apply_url || '');
    isValid = direct && apply;
  } else if (order.value.creation_source === 'file_import') {
    // Valid as soon as bytes are on the server — extraction finishes in the next step
    isValid = !fileIsUploading.value && (fileUploaded.value || !!fileImports.value.length);
  } else if (order.value.creation_source === 'web_import') {
    isValid = validateUrl(webImportUrl.value);
  } else if (order.value.creation_source === 'custom') {
    isValid = true; // Always valid when selected
  }

  allowNext.value = isValid;
  return isValid;
}

function onDirectLinkChange() {
  if (order.value.directlink) {
    order.value.directlink = stripHttps(order.value.directlink);
  }
  validateFields();
}

function onApplyLinkChange() {
  if (order.value.apply_url) order.value.apply_url = stripHttps(order.value.apply_url);
  validateFields();
}

function openApplyLinkForm() {
  showActiveLinkForm.value = true;
  validateFields();
}

function onWebImportUrlChange() {
  webImportUrl.value = stripHttps(webImportUrl.value);
  validateFields();
}

function normalizeActiveLinkValue() {
  if (order.value.creation_source === 'direct_url') onDirectLinkChange();
  else if (order.value.creation_source === 'web_import') onWebImportUrlChange();
}

function startLinkExtraction(forceValid = false) {
  if (forceValid || allowNext.value) {
    importStore.handleExtraction();
  }
}

async function onLinkPaste(event: ClipboardEvent) {
  const pasted = event.clipboardData?.getData('text')?.trim();
  if (!pasted) return;

  event.preventDefault();
  skipNextBlurExtraction.value = true;

  const normalized = stripHttps(pasted);
  if (order.value.creation_source === 'direct_url') {
    order.value.directlink = normalized;
  } else if (order.value.creation_source === 'web_import') {
    webImportUrl.value = normalized;
  }

  await nextTick();

  const isValid = validateFields();
  startLinkExtraction(isValid);
  (event.target as HTMLElement)?.blur?.();
}

function onLinkInput() {
  importStore.cancelActiveExtraction();
}

function onLinkBlur() {
  if (skipNextBlurExtraction.value) {
    skipNextBlurExtraction.value = false;
    return;
  }

  normalizeActiveLinkValue();
  startLinkExtraction();
}

function removeApplyLink() {
  order.value.apply_url = '';
  showActiveLinkForm.value = false;
  validateFields();
}

// $supportedExtensions
const allowedFileExtensions = ['pdf', 'docx'];
const fileAcceptAttribute =
  '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

function validateFileUpload(file: File): boolean {
  // Check file size (max 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) return false;

  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return allowedFileExtensions.includes(extension);
}

function onFileRemoved() {
  if (order.value.creation_source !== 'file_import') return;

  importStore.cancelFileImport();
  validateFields();
}

/* ---------------------------------- WATCH --------------------------------- */
watch(
  () => order.value.creation_source,
  () => validateFields(),
  { immediate: true }
);
watch([showActiveLinkForm, () => order.value.directlink, () => order.value.apply_url], () => {
  if (order.value.creation_source === 'direct_url') validateFields();
});
watch(fileImports, () => validateFields());
watch([fileUploaded, fileIsUploading], () => validateFields());
</script>

<style scoped>
.initial-step p,
.initial-step label,
.initial-step a {
  font-size: 13px;
}

.initial-step .border-primary {
  border-color: var(--tblr-primary) !important;
}

.option-card {
  position: relative;
  height: 100%;
  transition: all 0.2s ease-in-out;
  border: 2px solid #dee2e6;
  cursor: pointer;
}

.option-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.card-body {
  overflow-x: hidden;
}

.card-body .card-title {
  font-size: 12px;
}

.option-card .card-body .card-title {
  font-size: 12px;
  margin-bottom: 10px;
}

.option-card .card-body p {
  font-size: 11px;
}

.option-card .funnel-icon {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 18px;
  height: 18px;
  top: 7px;
  right: 7px;
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 24px;
}

.drop-zone {
  padding: 20px;
  border: 2px dashed #ced4da;
}

.section-divider {
  margin: 0 -2rem;
}

.remove-active-link {
  margin-top: 28px;
}

.initial-step :deep(.form-switch .form-check-input) {
  width: 40px;
  height: 24px;
  margin-right: 10px;
}

.initial-step :deep(.form-switch .form-check-input) {
  width: 48px;
  height: 24px;
  margin-right: 10px;
  background-size: contain;
}

.file-drop-zone {
  min-height: 120px;
}

.file-drop-zone:hover {
  border-color: #007bff;
}
</style>
